import { getCSRFToken } from '../utils/getCSRFToken';
import { processVerticalImageSmart } from '../utils/borderDetection';
import { parseDate } from '../utils/dateUtils';
import Pica from 'pica';
import Quill from 'quill'; // Make sure to import Quill

export default function registerStoreRateSetStore() {
  window.Alpine.store('storeRateSetStore', {
    calendarSlug: '',
    updateRateFormVisible: false,
    selectedRateSet: '',
    availableFeatures: [],
    formData: {
      public_name: '',
      internal_title: '',
      is_package: false,
      description: '',
      images: [],
      main_image: null,
      parent: null,
      features: [],
    },
    filteredStorePages: [],
    imageProcessing: false,
    imageAdded: '',
    quill: null,
    featureModalVisible: false,
    newFeature: {
      name: '',
      icon: 'fa-check',
    },
    // Extras functionality
    associatedExtras: [],
    extrasLoading: false,
    selectedExtras: {}, // Object to track selected quantities for each extra (current selection)
    selectedExtrasDropdowns: {}, // Object to track dropdown selections for each extra (current selection)
    addedExtraOptions: {}, // Object to track added extra options: { extraId: [{ option: 'S', quantity: 2 }, { option: 'L', quantity: 1 }] }

    // Add-on description modal
    showAddonDescriptionModal: false,
    selectedAddonForDescription: null,

    // Photo modal/lightbox
    photoModalOpen: false,
    photoModalImages: [],
    photoModalCurrentIndex: 0,
    photoModalAlt: '',

    // Related services navigation confirmation modal
    showRelatedServicesModal: false,
    pendingNavigationUrl: '',

    initQuill(element) {
      console.log('initQuill called with element:', element);

      if (this.quill) {
        console.log('Quill already initialized');
        return;
      }

      if (!element) {
        console.error('Element is null or undefined');
        return;
      }

      const toolbarOptions = [
        [{ header: [4, 5, false] }], // Only headers 4 and 5, and normal text
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ];

      const initQuillInstance = () => {
        console.log('Attempting to initialize Quill');
        try {
          this.quill = new Quill(element, {
            theme: 'snow',
            modules: {
              toolbar: toolbarOptions,
              clipboard: {
                matchVisual: false,
              },
            },
            formats: ['header', 'bold', 'italic', 'underline', 'list'],
          });

          console.log('Quill initialized successfully');

          // Delay setting content and focus
          setTimeout(() => {
            // Set initial content
            this.quill.root.innerHTML = this.formData.description || '';

            // Set focus to the editor
            this.quill.focus();

            console.log('Quill content and focus set');
          }, 0);

          // Update store when Quill content changes
          this.quill.on('text-change', () => {
            this.formData.description = this.quill.root.innerHTML;
          });
        } catch (error) {
          console.error('Error initializing Quill:', error);
        }
      };

      // Try to initialize immediately
      initQuillInstance();

      // If it fails, try again after a short delay
      if (!this.quill) {
        console.log('Quill not initialized, trying again after delay');
        setTimeout(initQuillInstance, 100);
      }
    },

    detectTransparency(canvas) {
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Check every 4th pixel (RGBA) for alpha channel
      // Skip some pixels for performance on large images
      const step = Math.max(
        1,
        Math.floor((canvas.width * canvas.height) / 10000),
      ); // Sample up to 10,000 pixels

      for (let i = 3; i < data.length; i += 4 * step) {
        if (data[i] < 255) {
          // Alpha channel less than 255 means transparency
          return true;
        }
      }

      return false;
    },

    async showUpdateForm(calendarSlug, rateSetId) {
      this.updateRateFormVisible = true;
      this.calendarSlug = calendarSlug;
      this.selectedRateSet = rateSetId;

      try {
        const featuresResponse = await fetch(
          `/bookings/${calendarSlug}/get_features/`,
        );
        const features = await featuresResponse.json();
        console.log('Available features:', features);
        this.availableFeatures = features;

        const rateSetResponse = await fetch(
          `/bookings/${calendarSlug}/${rateSetId}/get_rate_set_details_and_store_pages/`,
        );
        const data = await rateSetResponse.json();
        console.log('Rate set features:', data.rate_set.features);
        console.log('store_pages:', data.store_pages);
        // Populate StorePages
        this.filteredStorePages = data.store_pages;

        // Set current parent
        const rateSet = data.rate_set;
        this.formData.parent = rateSet.parent;

        this.formData.public_name = rateSet.public_name;
        this.formData.internal_title = rateSet.internal_title || '';
        this.formData.is_package = !!rateSet.is_package;
        this.formData.short_description = rateSet.short_description;
        this.formData.description = rateSet.description;
        this.formData.parent = rateSet.parent;

        // Ensure features are numbers if they're coming as strings
        this.formData.features = (rateSet.features || []).map((f) =>
          typeof f === 'object' ? f.id : parseInt(f, 10),
        );

        console.log('Processed features array:', this.formData.features);

        this.formData.images = rateSet.images.map((image) => ({
          id: image.id,
          url: image.image,
          is_main: image.is_main,
          file: null,
        }));

        const mainImageIndex = rateSet.images.findIndex(
          (image) => image.is_main,
        );
        this.formData.main_image =
          mainImageIndex !== -1 ? mainImageIndex : null;

        if (this.quill) {
          this.quill.root.innerHTML = this.formData.description;
        }

        this.formData = { ...this.formData };
      } catch (error) {
        console.error('Error in showUpdateForm:', error);
      }
    },
    showUpdateParentForm(calendarSlug, rateSetId) {
      this.updateParentFormVisible = true;
      console.log('rateSetId:', rateSetId);
      this.selectedRateSet = rateSetId;
      // Fetch only StorePages and current parent
      fetch(
        `/bookings/${calendarSlug}/${rateSetId}/get_rate_set_details_and_store_pages/`,
      )
        .then((response) => response.json())
        .then((data) => {
          // Populate StorePages
          this.filteredStorePages = data.store_pages;

          // Set current parent
          const rateSet = data.rate_set;
          this.formData.parent = rateSet.parent;

          // Ensure reactive update
          this.formData = { ...this.formData };
        })
        .catch((error) => {
          console.error(
            'Error fetching rate set details and store pages:',
            error,
          );
        });
    },
    submitUpdateForm(calendarSlug, rateSetId = null) {
      const effectiveRateSetId = rateSetId || this.selectedRateSet;
      if (!effectiveRateSetId) {
        console.error('No rate set selected for update');
        return;
      }
      const formData = new FormData();
      formData.append('public_name', this.formData.public_name);
      formData.append('internal_title', this.formData.internal_title || '');
      formData.append('short_description', this.formData.short_description);
      formData.append('description', this.formData.description);
      formData.append('main_image', this.formData.main_image);
      formData.append('parent', this.formData.parent);

      if (this.formData.features && this.formData.features.length > 0) {
        formData.append('features', JSON.stringify(this.formData.features));
      }

      this.formData.images.forEach((image, index) => {
        if (image.file) {
          formData.append(`images`, image.file);
        } else {
          formData.append(`existing_images[]`, image.id);
        }
      });

      // Hide modal before making the request
      this.updateRateFormVisible = false;

      fetch(
        `/bookings/${calendarSlug}/${effectiveRateSetId}/update_store_rate_sets/`,
        {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
          body: formData,
        },
      )
        .then((response) => {
          if (response.redirected) {
            // Add a small delay before redirect to ensure modal is fully hidden
            window.location.href = response.url;
          } else {
            return response.json().then((data) => {
              if (data.error) {
                console.error('Error:', data.error);
                // Show modal again if there's an error
                this.updateRateFormVisible = true;
              }
            });
          }
        })
        .catch((error) => {
          console.error('Error:', error);
          // Show modal again if there's an error
          this.updateRateFormVisible = true;
        });
    },
    submitUpdateParentForm(calendarSlug) {
      const formData = new FormData();
      formData.append('parent', this.formData.parent);
      const storePages = Alpine.store('storePages');
      console.log('storePages.items:', storePages.items);
      console.log(
        'storePages.storePageSelected:',
        storePages.storePageSelected,
      );

      fetch(
        `/bookings/${calendarSlug}/${this.selectedRateSet}/update_rate_set_parent/`,
        {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
          body: formData,
        },
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 'success') {
            const toastStore = Alpine.store('toastStore');
            console.log('data:', data);

            // Find the RateSet in the current items
            const index = storePages.items.findIndex(
              (item) => item.item.id === this.selectedRateSet,
            );

            if (index !== -1) {
              if (storePages.storePageSelected !== data.new_parent_id) {
                // If the parent has changed, remove the item from the current list
                console.log('Parent has changed, remove item!');

                // Add fade-out class to the element
                const element = document.querySelector(
                  `.page-square[data-id="${this.selectedRateSet}"]`,
                );
                if (element) {
                  element.classList.add('fade-out');
                  setTimeout(() => {
                    storePages.items.splice(index, 1);
                  }, 500); // Match this to the CSS transition duration
                }
                if (toastStore && toastStore.addToast) {
                  toastStore.addToast(
                    `${data.rate_set_title} was moved to ${data.new_parent_title}`,
                    'success',
                  );
                }
              }
            }

            this.updateParentFormVisible = false;
            this.resetForm();
          } else {
            console.error('Error:', data.error);
          }
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    },
    async handleFileUpload(event) {
      const files = event.target.files;
      const maxFiles = 10;

      // Check if adding these files would exceed the limit
      if (this.formData.images.length + files.length > maxFiles) {
        alert('Maximum 10 images allowed per Listing');
        event.target.value = ''; // Clear the input
        return;
      }

      const pica = new Pica({
        features: ['js', 'wasm'], // Use JS and WASM but not web workers due to CSP restrictions
      });
      const maxSizeInBytes = 500 * 1024; // 500 KB
      const maxWidth = 1920; // Maximum width for resizing
      const maxHeight = 1080; // Maximum height for resizing

      this.imageProcessing = true;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();

        reader.onload = async (e) => {
          const img = new Image();
          img.src = e.target.result;
          img.onload = async () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d', { willReadFrequently: true });

            console.log(
              'Image processing - Dimensions:',
              img.width,
              'x',
              img.height,
            );

            const aspectRatio = img.width / img.height;
            const isTallerThan16_9 = aspectRatio < 16 / 9;

            if (isTallerThan16_9) {
              // Images taller than 16:9: Use smart processing for transparent/uniform border cases
              console.log(
                'Processing image taller than 16:9 with smart border detection',
              );
              processVerticalImageSmart(img, ctx, canvas);
            } else {
              // Square and Horizontal images: NO PROCESSING - use original
              console.log('Using original image - no processing needed');

              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0, img.width, img.height);
            }

            // Detect if image has transparency
            const hasTransparencyFlag = this.detectTransparency(canvas);

            // Choose format based on transparency
            const format = hasTransparencyFlag ? 'image/png' : 'image/jpeg';
            const quality = hasTransparencyFlag ? undefined : 0.9; // JPEG quality

            let blob = await new Promise((resolve) => {
              canvas.toBlob(resolve, format, quality);
            });

            // Resize if the file size is greater than maxSizeInBytes or dimensions exceed maxWidth/maxHeight
            if (
              blob.size > maxSizeInBytes ||
              canvas.width > maxWidth ||
              canvas.height > maxHeight
            ) {
              const resizedCanvas = document.createElement('canvas');
              const width = canvas.width;
              const height = canvas.height;
              const scaleFactor = Math.min(
                maxWidth / width,
                maxHeight / height,
                Math.sqrt(maxSizeInBytes / blob.size),
              );
              resizedCanvas.width = width * scaleFactor;
              resizedCanvas.height = height * scaleFactor;

              await pica.resize(canvas, resizedCanvas);

              // Use the same format detection for resized image
              const resizedHasTransparencyFlag =
                this.detectTransparency(resizedCanvas);
              const resizedFormat = resizedHasTransparencyFlag
                ? 'image/png'
                : 'image/jpeg';
              const resizedQuality = resizedHasTransparencyFlag
                ? undefined
                : 0.9;

              blob = await new Promise((resolve) => {
                resizedCanvas.toBlob(resolve, resizedFormat, resizedQuality);
              });
            }

            // Create filename with correct extension based on format
            const extension = hasTransparencyFlag ? '.png' : '.jpg';
            const fileName = file.name.replace(/\.[^/.]+$/, '') + extension;
            const fileWithExt = new File([blob], fileName, {
              type: format,
            });

            this.formData.images.push({
              file: fileWithExt,
              url: canvas.toDataURL(format, quality),
            });

            // Automatically select the first uploaded image as the main image if none is selected
            if (this.formData.main_image === null) {
              this.formData.main_image = this.formData.images.length - 1;
            }

            // Ensure reactive update
            this.formData = { ...this.formData };

            // Set imageProcessing to false when all files are processed
            if (i === files.length - 1) {
              this.imageProcessing = false;
              this.imageAdded = file.name;
            }
          };
        };

        reader.readAsDataURL(file);
      }
    },

    removeImage(index) {
      this.formData.images.splice(index, 1);
      // Adjust the main_image index if necessary
      if (this.formData.main_image === index) {
        this.formData.main_image = this.formData.images.length ? 0 : null;
      } else if (this.formData.main_image > index) {
        this.formData.main_image -= 1;
      }
      // Ensure reactive update
      this.formData = { ...this.formData };
    },

    unpublishRateSet() {
      const toastStore = Alpine.store('toastStore');
      const storePages = Alpine.store('storePages');

      fetch(`/rates/unpublish_rateset/${this.selectedRateSet}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Failed to unpublish Rate');
          return response.json();
        })
        .then((data) => {
          console.log('Rate Unpublished successfully:', data);
          this.updateParentFormVisible = false;

          // Find the RateSet in the current items
          const index = storePages.items.findIndex(
            (item) => item.item.id === this.selectedRateSet,
          );

          if (index !== -1) {
            // Add fade-out class to the element
            const element = document.querySelector(
              `.page-square[data-id="${this.selectedRateSet}"]`,
            );
            if (element) {
              element.classList.add('fade-out');
              setTimeout(() => {
                storePages.items.splice(index, 1);
              }, 500); // Match this to the CSS transition duration
            }
          }

          if (toastStore && toastStore.addToast) {
            toastStore.addToast(
              `${data.rate_set_public_name} was Unpublished successfully`,
              'success',
            );
          }
        })
        .catch((error) => {
          console.error('Error unpublishing Rate:', error);
        });
    },

    resetForm() {
      this.formData = {
        public_name: '',
        internal_title: '',
        is_package: false,
        description: '',
        images: [],
        main_image: null,
        parent: null,
      };
      this.imageAdded = '';
    },

    showFeatureModal() {
      console.log('Opening feature modal');
      this.featureModalVisible = true;
      // Reset the form data
      this.newFeature = {
        name: '',
        icon: 'fa-check',
      };
      // Force a re-render
      setTimeout(() => {
        const modal = document.querySelector('.modal.fade.show');
        if (modal) {
          modal.style.display = 'block';
        }
      }, 50);
    },

    closeFeatureModal() {
      this.featureModalVisible = false;
      const modal = document.querySelector('.modal.fade.show');
      if (modal) {
        modal.style.display = 'none';
      }
      // Return focus to the Add Feature button
      const addFeatureBtn = document.querySelector('[data-feature-add-btn]');
      if (addFeatureBtn) addFeatureBtn.focus();
    },

    async submitFeature() {
      try {
        const response = await fetch(
          `/bookings/${this.calendarSlug}/create_feature/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': document.querySelector(
                '[name=csrfmiddlewaretoken]',
              ).value,
            },
            body: JSON.stringify({
              name: this.newFeature.name,
              icon: this.newFeature.icon,
            }),
          },
        );

        if (response.ok) {
          const newFeature = await response.json();
          this.availableFeatures.push(newFeature);
          this.featureModalVisible = false;
          // Optional: Show success message
        } else {
          // Handle error
          console.error('Failed to create feature');
        }
      } catch (error) {
        console.error('Error creating feature:', error);
      }
    },

    // Extras functionality methods
    getAssociatedExtras() {
      return Array.isArray(this.associatedExtras) ? this.associatedExtras : [];
    },

    async fetchAssociatedExtras(calendarId, rateSetId) {
      this.extrasLoading = true;

      // Reset any previous selections first
      this.resetSelectedExtras();

      try {
        const response = await fetch(
          `/calendar/rate-set-extras/${calendarId}/${rateSetId}/`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          this.associatedExtras = data.extras || [];
          // Initialize selectedExtras with 0 for each extra
          this.selectedExtras = {};
          this.selectedExtrasDropdowns = {};
          this.addedExtraOptions = {};
          this.associatedExtras.forEach((extra) => {
            this.selectedExtras[extra.id] = 0;
            this.selectedExtrasDropdowns[extra.id] = '';
            this.addedExtraOptions[extra.id] = [];
          });
          console.log(
            'Initialized selectedExtrasDropdowns:',
            this.selectedExtrasDropdowns,
          );
        } else {
          console.error('Failed to fetch associated extras');
          this.associatedExtras = [];
          this.selectedExtras = {};
          this.selectedExtrasDropdowns = {};
          this.addedExtraOptions = {};
        }
      } catch (error) {
        console.error('Error fetching associated extras:', error);
        this.associatedExtras = [];
        this.selectedExtras = {};
        this.selectedExtrasDropdowns = {};
        this.addedExtraOptions = {};
      } finally {
        this.extrasLoading = false;
      }
    },

    updateExtraQuantity(extraId, quantity) {
      // Ensure quantity is at least 0
      const validQuantity = Math.max(0, parseInt(quantity) || 0);
      this.selectedExtras[extraId] = validQuantity;

      // Trigger price update effect in storeBookingForm
      const storeBookingForm = Alpine.store('storeBookingForm');
      if (storeBookingForm && storeBookingForm.triggerPriceUpdateEffect) {
        storeBookingForm.triggerPriceUpdateEffect();
      }
    },

    updateExtraDropdown(extraId, value) {
      // Update dropdown selection for an extra
      this.selectedExtrasDropdowns[extraId] = value;
      console.log('Updated dropdown for extra', extraId, 'to:', value);

      // Trigger price update effect in storeBookingForm
      const storeBookingForm = Alpine.store('storeBookingForm');
      if (storeBookingForm && storeBookingForm.triggerPriceUpdateEffect) {
        storeBookingForm.triggerPriceUpdateEffect();
      }
    },

    handleAutoAddOption(extraId, selectedValue) {
      if (!selectedValue) {
        // Clear the dropdown if empty option selected
        this.selectedExtrasDropdowns[extraId] = '';
        return;
      }

      // Update the dropdown value
      this.selectedExtrasDropdowns[extraId] = selectedValue;

      // Automatically add the selected option
      if (!this.addedExtraOptions[extraId]) {
        this.addedExtraOptions[extraId] = [];
      }

      // Check if this option already exists
      const existingOption = this.addedExtraOptions[extraId].find(
        (opt) => opt.option === selectedValue,
      );
      if (existingOption) {
        // Increment existing option quantity
        existingOption.quantity += 1;
      } else {
        // Add new option with quantity 1
        this.addedExtraOptions[extraId].push({
          option: selectedValue,
          quantity: 1,
        });
      }

      // Clear the dropdown selection for next selection
      this.selectedExtrasDropdowns[extraId] = '';

      console.log('Auto-added option:', selectedValue, 'for extra', extraId);
      console.log('Current added options:', this.addedExtraOptions[extraId]);

      // Trigger price update effect in storeBookingForm
      const storeBookingForm = Alpine.store('storeBookingForm');
      if (storeBookingForm && storeBookingForm.triggerPriceUpdateEffect) {
        storeBookingForm.triggerPriceUpdateEffect();
      }
    },

    addSelectedExtraOption(extraId) {
      const selectedOption = this.selectedExtrasDropdowns[extraId];

      if (!selectedOption) {
        console.log('No option selected for extra', extraId);
        return;
      }

      if (!this.addedExtraOptions[extraId]) {
        this.addedExtraOptions[extraId] = [];
      }

      // Check if this option already exists
      const existingIndex = this.addedExtraOptions[extraId].findIndex(
        (item) => item.option === selectedOption,
      );

      if (existingIndex >= 0) {
        // Option already exists, increment quantity
        this.addedExtraOptions[extraId][existingIndex].quantity += 1;
        console.log(
          `Incremented ${selectedOption} quantity to ${this.addedExtraOptions[extraId][existingIndex].quantity} for extra ${extraId}`,
        );
      } else {
        // Add new option with quantity 1
        this.addedExtraOptions[extraId].push({
          option: selectedOption,
          quantity: 1,
        });
        console.log(
          `Added new option ${selectedOption} with quantity 1 for extra ${extraId}`,
        );
      }

      // Clear the dropdown after adding
      this.selectedExtrasDropdowns[extraId] = '';

      // Trigger price update effect in storeBookingForm
      const storeBookingForm = Alpine.store('storeBookingForm');
      if (storeBookingForm && storeBookingForm.triggerPriceUpdateEffect) {
        storeBookingForm.triggerPriceUpdateEffect();
      }
    },

    // Option B: Dynamic Add Button methods
    addAnotherExtraOption(extraId) {
      const selectedOption = this.selectedExtrasDropdowns[extraId];
      const selectedQuantity = this.selectedExtras[extraId];

      if (selectedOption && selectedQuantity > 0) {
        // Initialize array if it doesn't exist
        if (!this.addedExtraOptions[extraId]) {
          this.addedExtraOptions[extraId] = [];
        }

        // Add the current selection to the added options
        this.addedExtraOptions[extraId].push({
          option: selectedOption,
          quantity: selectedQuantity,
        });

        // Reset current selection
        this.selectedExtras[extraId] = 0;
        this.selectedExtrasDropdowns[extraId] = '';

        console.log(
          `Added ${selectedQuantity}x ${selectedOption} for extra ${extraId}`,
        );

        // Trigger price update effect in storeBookingForm
        const storeBookingForm = Alpine.store('storeBookingForm');
        if (storeBookingForm && storeBookingForm.triggerPriceUpdateEffect) {
          storeBookingForm.triggerPriceUpdateEffect();
        }
      }
    },

    removeAddedExtraOption(extraId, index) {
      if (
        this.addedExtraOptions[extraId] &&
        this.addedExtraOptions[extraId][index]
      ) {
        this.addedExtraOptions[extraId].splice(index, 1);
        console.log(
          `Removed added option at index ${index} for extra ${extraId}`,
        );

        // Trigger price update effect in storeBookingForm
        const storeBookingForm = Alpine.store('storeBookingForm');
        if (storeBookingForm && storeBookingForm.triggerPriceUpdateEffect) {
          storeBookingForm.triggerPriceUpdateEffect();
        }
      }
    },

    updateAddedExtraQuantity(extraId, index, newQuantity) {
      const validQuantity = Math.max(0, parseInt(newQuantity) || 0);

      if (
        this.addedExtraOptions[extraId] &&
        this.addedExtraOptions[extraId][index]
      ) {
        if (validQuantity === 0) {
          // Remove the item if quantity is 0
          this.addedExtraOptions[extraId].splice(index, 1);
          console.log(
            `Removed added option at index ${index} for extra ${extraId} (quantity set to 0)`,
          );
        } else {
          // Update the quantity
          this.addedExtraOptions[extraId][index].quantity = validQuantity;
          console.log(
            `Updated added option quantity to ${validQuantity} for extra ${extraId}`,
          );
        }
      }
    },

    getAddedExtraOptions(extraId) {
      return this.addedExtraOptions[extraId] || [];
    },

    getExtraPriceDisplay(extra, dropdownSelection = '') {
      const basePrice = parseFloat(extra.unit_price);

      // Check if we have booking dates to calculate duration
      if (
        extra.pricing_basis === 'per_day' ||
        extra.pricing_basis === 'per_night'
      ) {
        // Get dates from storeBookingForm since that's where the date selection is managed
        const bookingForm = window.Alpine.store('storeBookingForm');
        const startDate = bookingForm.startDate;
        const endDate = bookingForm.endDate;

        if (startDate && startDate !== '') {
          const start = parseDate(startDate);
          let end = parseDate(endDate || startDate); // Use startDate as fallback for single-day tours

          console.log('🔍 [storeRateSetStore] Date Calculation Debug:');
          console.log('  Raw startDate:', startDate);
          console.log('  Raw endDate:', endDate);
          console.log('  Parsed start:', start);
          console.log('  Parsed end:', end);

          // Validate dates
          if (
            start &&
            end &&
            !isNaN(start.getTime()) &&
            !isNaN(end.getTime())
          ) {
            // Calculate days more reliably by counting actual days
            const startDateOnly = new Date(
              start.getFullYear(),
              start.getMonth(),
              start.getDate(),
            );
            const endDateOnly = new Date(
              end.getFullYear(),
              end.getMonth(),
              end.getDate(),
            );

            let daysDiff = 0;
            const currentDate = new Date(startDateOnly);
            while (currentDate < endDateOnly) {
              daysDiff++;
              currentDate.setDate(currentDate.getDate() + 1);
            }

            console.log('  Start date:', startDateOnly.toDateString());
            console.log('  End date:', endDateOnly.toDateString());
            console.log('  Days difference (calculated):', daysDiff);

            // Handle date calculation based on accommodation vs tour
            const isAccommodation =
              this.rateSet && this.rateSet.rate_set_type === 'accommodation';

            if (daysDiff === 0) {
              daysDiff = 1; // Single day selection
              console.log('  Single day selection: set to 1 day');
            } else if (isAccommodation) {
              // For accommodations: 28th-30th = 2 days difference = 2 nights (use as-is)
              console.log(
                '  Accommodation: using duration as nights (no adjustment)',
              );
            } else {
              // For tours: 15th-17th = 2 days difference = 3 days inclusive (add 1)
              daysDiff += 1;
              console.log('  Tour: added 1 for inclusive range');
            }

            if (daysDiff > 0) {
              // Apply duration adjustment (can be positive or negative)
              let adjustedDuration = daysDiff;
              if (extra.duration_adjustment) {
                adjustedDuration = daysDiff + extra.duration_adjustment;
              }

              // Ensure minimum of 1 day/night
              adjustedDuration = Math.max(1, adjustedDuration);

              const totalPrice = basePrice * adjustedDuration;
              // Determine unit based on extra's pricing_basis setting
              let pricingUnit, unitText;

              if (extra.pricing_basis === 'per_night') {
                pricingUnit = 'night';
                unitText =
                  adjustedDuration === 1 ? pricingUnit : `${pricingUnit}s`;
              } else {
                // Default to 'per_day' for any other case (including 'per_day')
                pricingUnit = 'day';
                unitText =
                  adjustedDuration === 1 ? pricingUnit : `${pricingUnit}s`;
              }

              // Format: "Wetsuit (S) - 3 days" or "Wetsuit - 3 nights" based on pricing_basis
              const baseName = dropdownSelection
                ? `${extra.name} (${dropdownSelection})`
                : extra.name;
              return {
                title: `${baseName} - ${adjustedDuration} ${unitText}`,
                price: `€${totalPrice.toFixed(2)}`,
              };
            }
          }
        }

        // Fallback if no dates available
        let pricingUnit;
        if (extra.pricing_basis === 'per_night') {
          pricingUnit = 'night';
        } else {
          pricingUnit = 'day';
        }

        const baseName = dropdownSelection
          ? `${extra.name} (${dropdownSelection})`
          : extra.name;
        return {
          title: `${baseName} per ${pricingUnit}`,
          price: `€${basePrice.toFixed(2)}`,
        };
      }

      // For per_item pricing, just show the base price
      const baseName = dropdownSelection
        ? `${extra.name} (${dropdownSelection})`
        : extra.name;
      return {
        title: baseName,
        price: `€${basePrice.toFixed(2)}`,
      };
    },

    getSelectedExtras() {
      // Return array of extras with their selected quantities and dropdown selections
      const selectedExtras = [];
      const extras = this.getAssociatedExtras();

      // Process each extra
      extras.forEach((extra) => {
        // Add current selection (if any)
        const currentQuantity = this.selectedExtras[extra.id] || 0;
        if (currentQuantity > 0) {
          selectedExtras.push({
            extra_id: extra.id,
            quantity: currentQuantity,
            dropdown_selection: this.selectedExtrasDropdowns[extra.id] || '',
            extra: extra,
          });
        }

        // Add all previously added options
        const addedOptions = this.addedExtraOptions[extra.id] || [];
        addedOptions.forEach((addedOption) => {
          selectedExtras.push({
            extra_id: extra.id,
            quantity: addedOption.quantity,
            dropdown_selection: addedOption.option,
            extra: extra,
          });
        });
      });

      return selectedExtras;
    },

    hasSelectedExtras() {
      const selectedExtras = this.getSelectedExtras();
      return selectedExtras.length > 0;
    },

    resetSelectedExtras() {
      Object.keys(this.selectedExtras).forEach((extraId) => {
        this.selectedExtras[extraId] = 0;
      });
      // Also reset dropdown selections
      Object.keys(this.selectedExtrasDropdowns).forEach((extraId) => {
        this.selectedExtrasDropdowns[extraId] = '';
      });
      // Reset added options
      Object.keys(this.addedExtraOptions).forEach((extraId) => {
        this.addedExtraOptions[extraId] = [];
      });
    },

    clearAllExtrasState() {
      // Completely clear all extras-related state
      this.associatedExtras = [];
      this.selectedExtras = {};
      this.selectedExtrasDropdowns = {};
      this.addedExtraOptions = {};
      this.extrasLoading = false;
    },

    // Add-on description modal methods
    openAddonDescriptionModal(extra) {
      this.selectedAddonForDescription = extra;
      this.showAddonDescriptionModal = true;
    },

    // Photo modal/lightbox methods
    openPhotoModal(index, images, alt = '') {
      console.log(
        'Opening photo modal with index:',
        index,
        'images:',
        images,
        'alt:',
        alt,
      );
      this.photoModalImages = images;
      this.photoModalCurrentIndex = index;
      this.photoModalAlt = alt;
      this.photoModalOpen = true;

      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      // Add touch event listeners for swipe navigation
      this.addTouchListeners();
    },

    closePhotoModal() {
      this.photoModalOpen = false;
      // Restore body scroll
      document.body.style.overflow = '';

      // Remove touch event listeners
      this.removeTouchListeners();
    },

    nextPhoto() {
      if (this.photoModalCurrentIndex < this.photoModalImages.length - 1) {
        this.photoModalCurrentIndex++;
      } else {
        this.photoModalCurrentIndex = 0; // Loop to first image
      }
    },

    previousPhoto() {
      if (this.photoModalCurrentIndex > 0) {
        this.photoModalCurrentIndex--;
      } else {
        this.photoModalCurrentIndex = this.photoModalImages.length - 1; // Loop to last image
      }
    },

    goToPhoto(index) {
      this.photoModalCurrentIndex = index;
    },

    // Touch/swipe navigation methods
    addTouchListeners() {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;

      const modalContent = document.querySelector('.photo-modal-content');
      if (modalContent) {
        modalContent.addEventListener(
          'touchstart',
          this.handleTouchStart.bind(this),
          { passive: true },
        );
        modalContent.addEventListener(
          'touchend',
          this.handleTouchEnd.bind(this),
          { passive: true },
        );
      }
    },

    removeTouchListeners() {
      const modalContent = document.querySelector('.photo-modal-content');
      if (modalContent) {
        modalContent.removeEventListener(
          'touchstart',
          this.handleTouchStart.bind(this),
        );
        modalContent.removeEventListener(
          'touchend',
          this.handleTouchEnd.bind(this),
        );
      }
    },

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
    },

    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.touchEndY = e.changedTouches[0].screenY;
      this.handleSwipe();
    },

    handleSwipe() {
      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;

      // Only process horizontal swipes (ignore vertical scrolling)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          // Swipe right - go to previous photo
          this.previousPhoto();
        } else {
          // Swipe left - go to next photo
          this.nextPhoto();
        }
      }
    },

    get currentPhoto() {
      return this.photoModalImages[this.photoModalCurrentIndex] || '';
    },

    // Related services navigation confirmation modal methods
    openRelatedServicesModal(url) {
      this.pendingNavigationUrl = url;
      this.showRelatedServicesModal = true;
    },

    confirmRelatedServicesNavigation() {
      // Clear the booking form before redirecting
      const bookingFormStore = Alpine.store('storeBookingForm');
      if (bookingFormStore) {
        // Reset form fields to their initial state
        bookingFormStore.startDate = '';
        bookingFormStore.endDate = '';
        bookingFormStore.selectedPeople = null;
        bookingFormStore.selectedAdults = 0;
        bookingFormStore.selectedChildren = 0;
        bookingFormStore.units = 1;
        bookingFormStore.customDropdownSelected = null;
        bookingFormStore.price = 0;
        bookingFormStore.priceUpdated = false;

        // Clear any selected extras
        const storeRateSetStore = Alpine.store('storeRateSetStore');
        if (storeRateSetStore) {
          storeRateSetStore.selectedExtras = {};
          storeRateSetStore.selectedExtrasDropdowns = {};
          storeRateSetStore.addedExtraOptions = {};
        }
      }

      // Now redirect
      window.location.href = this.pendingNavigationUrl;
    },

    cancelRelatedServicesNavigation() {
      this.showRelatedServicesModal = false;
      this.pendingNavigationUrl = '';
    },

    handleRelatedServiceClick(url) {
      // Check if the booking form has any changes
      const bookingFormStore = Alpine.store('storeBookingForm');
      if (bookingFormStore && bookingFormStore.hasFormChanges()) {
        // Show confirmation modal
        this.openRelatedServicesModal(url);
      } else {
        // No changes, navigate directly
        window.location.href = url;
      }
    },
  });
}
